import React from 'react';
import { Button, IconButton, Box } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { useUnitOverride, uploadUnitAsset, updateUnitContent } from '../../services/unitOverridesService';

const PAGE_ID = 'm3_u1_x1_freie_kaelte';

const images = [
  { key: 'frost', src: '/assets/drawable/frost.jpeg', alt: 'Frost Symbol', label: 'Frost' },
  { key: 'solekreislauf', src: '/assets/drawable/m3_kalte_solekreislauf.jpg', alt: 'Solekreislauf Schema', label: 'Solekreislauf' }
];

const textBlocks = [
  {
    id: 'block_intro',
    heading: 'Freie Kühlung: Die Natur als Klimaanlage nutzen',
    body:
      'Stell dir vor, du könntest dein Gebäude kühlen, ohne dafür teure und energieintensive Klimaanlagen ständig laufen zu lassen. ' +
      'Genau das ist die Idee hinter der "Freien Kühlung"! Wir nutzen dabei, was die Natur uns kostenlos zur Verfügung stellt: ' +
      'kühle Außenluft, die konstante Temperatur des Erdreichs oder kühles Grundwasser.'
  },
  {
    id: 'block_grundprinzip',
    heading: 'Das Grundprinzip: Kälte ernten statt erzeugen',
    body:
      'Anstatt Kälte aktiv mit viel Energieaufwand durch Kompressoren (wie im Kühlschrank oder einer typischen Klimaanlage) zu erzeugen, ' +
      '"ernten" wir bei der freien Kühlung vorhandene Kälte aus natürlichen Quellen. Das spart enorm viel Energie und schont damit nicht nur den Geldbeutel, ' +
      'sondern auch die Umwelt. Die einzige Energie, die wir meistens noch brauchen, ist ein bisschen Strom für Ventilatoren oder Pumpen, ' +
      'um die Kälte dorthin zu transportieren, wo sie gebraucht wird.'
  },
  {
    id: 'block_nacht_funktionsprinzip',
    heading: '1. Nachtauskühlung: Frische Brise für kühle Räume – Funktionsprinzip',
    body:
      'Nachts, wenn die Außentemperaturen deutlich sinken, öffnen wir Fenster oder nutzen Lüftungsanlagen, um die kühle Nachtluft ins Gebäude zu leiten. ' +
      'Diese kühle Luft verdrängt die tagsüber aufgestaute warme Luft. Zusätzlich können massive Bauteile im Gebäude (Wände, Decken, Böden) die Kälte der Nacht "speichern" ' +
      'und über den nächsten Tag langsam wieder abgeben. Das ist wie ein natürlicher Kältespeicher.'
  },
  {
    id: 'block_nacht_bauteile',
    heading: 'Nachtauskühlung – Benötigte Bauteile',
    body:
      '• Steuerbare Fenster (manuell oder automatisch)\n' +
      '• Lüftungsanlage mit steuerbaren Klappen (Zu-/Abluft)\n' +
      '• Temperatursensoren (innen und außen)\n' +
      '• Ggf. CO2-Sensoren zur Optimierung der Luftqualität\n' +
      '• Ggf. Regensensor und Windsensor (um Fenster bei schlechtem Wetter zu schließen)\n' +
      '• Regelungseinheit/Gebäudeautomation'
  },
  {
    id: 'block_nacht_regelung',
    heading: 'Nachtauskühlung – Regelung',
    body:
      'Die Regelung prüft typischerweise folgende Bedingungen:\n' +
      '1. Ist es draußen kälter als drinnen? (z.B. Außentemperatur < Innentemperatur - 2°C)\n' +
      '2. Ist die Außentemperatur niedrig genug? (z.B. < 18°C, um nicht zu stark auszukühlen)\n' +
      '3. Ist die gewünschte Raumtemperatur noch nicht erreicht?\n' +
      '4. Ist es Nacht oder sind die Räume ungenutzt? (Zeitprogramm)\n' +
      '5. Regnet es nicht? Ist der Wind nicht zu stark? (Schutzfunktion)\n\n' +
      'Sind die Bedingungen erfüllt, werden die Fenster oder Lüftungsklappen geöffnet. Die Lüfter der Anlage laufen dann meist auf einer angepassten Stufe. ' +
      'Sobald die gewünschte Innentemperatur erreicht ist oder es morgens wieder wärmer wird, schließt das System die Öffnungen.'
  },
  {
    id: 'block_erdwaerme_funktionsprinzip',
    heading: '2. Erdwärme (Geothermie): Kühle aus der Tiefe – Funktionsprinzip',
    body:
      'Das Erdreich hat ab einer gewissen Tiefe (ca. 1,5 - 2 Meter) eine relativ konstante Temperatur über das ganze Jahr (oft zwischen 8-12°C in unseren Breitengraden). ' +
      'Diese natürliche Kühle können wir nutzen. Über Erdsonden (tiefe Bohrungen) oder Erdkollektoren (flächig verlegte Rohre) wird eine Flüssigkeit ' +
      '(Sole = Wasser-Frostschutz-Gemisch) gepumpt. Diese Sole nimmt die Kühle aus dem Erdreich auf und transportiert sie ins Gebäude, wo sie dann z.B. über eine Fußbodenheizung ' +
      '(die dann zur Fußbodenkühlung wird) oder Gebläsekonvektoren an die Räume abgegeben wird. Dies nennt man oft "passive" oder "stille" Kühlung, wenn keine Wärmepumpe aktiv Kälte erzeugt.'
  },
  {
    id: 'block_erdwaerme_bauteile',
    heading: 'Erdwärme – Benötigte Bauteile',
    body:
      '• Erdsonden oder Erdkollektoren\n' +
      '• Solekreislauf mit Umwälzpumpe\n' +
      '• Wärmeübertrager (trennt Solekreis vom Heiz-/Kühlkreis im Gebäude)\n' +
      '• Heiz-/Kühlkreis im Gebäude (z.B. Fußbodenheizung, Gebläsekonvektoren)\n' +
      '• Temperatursensoren (Sole, Raum, Vorlauf/Rücklauf Kühlkreis)\n' +
      '• Regelungseinheit (oft Teil der Wärmepumpenregelung oder separate GLT)\n' +
      '• Ggf. 3-Wege-Ventil zur Umschaltung zwischen Heiz- und Kühlbetrieb der Wärmequelle'
  },
  {
    id: 'block_erdwaerme_regelung',
    heading: 'Erdwärme – Regelung',
    body:
      '1. Besteht Kühlbedarf im Raum? (Raumtemperatur > Solltemperatur + Hysterese)\n' +
      '2. Ist die Soletemperatur niedrig genug, um effektiv zu kühlen? (z.B. Soletemperatur < Raumtemperatur - 5°C)\n' +
      '3. Taupunktüberwachung: Besonders wichtig bei Flächenkühlung! Die Vorlauftemperatur des Kühlwassers darf nicht so niedrig sein, dass sich Kondenswasser auf den kühlen Flächen bildet. ' +
      'Ein Taupunktsensor im Raum oder eine Berechnung aus Raumtemperatur und Luftfeuchtigkeit steuert das. Die Vorlauftemperatur wird dann so angepasst, dass sie immer sicher über dem Taupunkt liegt.\n\n' +
      'Wenn die Bedingungen erfüllt sind, wird die Solepumpe aktiviert und Ventile so gestellt, dass die Kühle aus dem Erdreich in den Kühlkreis des Gebäudes gelangt. ' +
      'Die Wärmepumpe selbst bleibt bei reiner passiver Kühlung aus.'
  },
  {
    id: 'block_grundwasser_funktionsprinzip',
    heading: '3. Grundwasserkühlung: Die unterirdische Frischequelle – Funktionsprinzip',
    body:
      'Ähnlich wie bei der Erdwärme hat auch Grundwasser eine recht konstante, kühle Temperatur (oft 7-12°C). Über einen Förderbrunnen wird kühles Grundwasser entnommen, ' +
      'über einen Wärmeübertrager gibt es seine Kälte an den Kühlkreislauf des Gebäudes ab und wird dann über einen Schluckbrunnen wieder dem Grundwasserleiter zugeführt. ' +
      'Wichtig ist, dass Entnahme und Rückgabe hydrogeologisch unbedenklich sind.'
  },
  {
    id: 'block_grundwasser_bauteile',
    heading: 'Grundwasserkühlung – Benötigte Bauteile',
    body:
      '• Förderbrunnen mit Unterwasserpumpe\n' +
      '• Schluckbrunnen\n' +
      '• Wärmeübertrager (Systemtrennung zum Schutz des Grundwassers und des Gebäudekreises)\n' +
      '• Rohrleitungen für Grundwasser und Gebäudekühlkreis\n' +
      '• Filter (um Verunreinigungen aus dem Grundwasser fernzuhalten)\n' +
      '• Temperatursensoren, Drucksensoren, Durchflussmesser\n' +
      '• Regelungseinheit'
  },
  {
    id: 'block_grundwasser_regelung',
    heading: 'Grundwasserkühlung – Regelung',
    body:
      '1. Kühlbedarf im Raum vorhanden?\n' +
      '2. Grundwassertemperatur ausreichend niedrig?\n' +
      '3. Genehmigungsauflagen beachten: Oft gibt es Vorgaben zur maximalen Entnahmemenge oder zur Temperaturdifferenz zwischen Entnahme und Rückgabe.\n' +
      '4. Taupunktüberwachung im Gebäudekühlkreis.\n\n' +
      'Bei Kühlbedarf startet die Brunnenpumpe. Das Grundwasser fließt durch den Wärmeübertrager und kühlt das Wasser im Gebäudekreislauf. ' +
      'Die Regelung überwacht Temperaturen und Durchflüsse und stellt sicher, dass alle Betriebsparameter eingehalten werden.'
  },
  {
    id: 'block_aussenluft_funktionsprinzip',
    heading: '4. Außenluftkühlung (Lüftungsanlage): Kühle Luft direkt nutzen – Funktionsprinzip',
    body:
      'Wenn die Außenluft kühler ist als die gewünschte Raumtemperatur (und auch kühler als die Abluft), kann eine Lüftungsanlage diese direkt zur Kühlung nutzen. ' +
      'Die warme Raumabluft wird abgeführt und durch kühle Außenluft ersetzt. Dies geschieht oft in der Übergangszeit (Frühling, Herbst) oder an kühleren Sommertagen. ' +
      'Moderne Lüftungsanlagen haben oft einen Bypass für den Wärmerückgewinner: Im Sommer wird dieser Bypass geöffnet, damit die kühle Außenluft nicht von der warmen Abluft wieder aufgewärmt wird.'
  },
  {
    id: 'block_aussenluft_bauteile',
    heading: 'Außenluftkühlung – Benötigte Bauteile',
    body:
      '• Zentrale oder dezentrale Lüftungsanlage\n' +
      '• Steuerbare Zuluft- und Abluftventilatoren\n' +
      '• Filter für Außen- und Abluft\n' +
      '• Temperatursensoren (Außenluft, Zuluft, Raumluft, Abluft)\n' +
      '• Ggf. Feuchtesensoren\n' +
      '• Bypassklappe für den Wärmerückgewinner (WRG)\n' +
      '• Regelungseinheit'
  },
  {
    id: 'block_aussenluft_regelung',
    heading: 'Außenluftkühlung – Regelung',
    body:
      '1. Kühlbedarf im Raum? (Raumtemperatur > Solltemperatur)\n' +
      '2. Ist die Außenluft kälter als die Raumluft? (z.B. Außentemperatur < Raumtemperatur - 2°C)\n' +
      '3. Ist die Außenluft nicht zu kalt und nicht zu feucht? (Komfortgrenzen)\n' +
      '4. WRG-Bypass öffnen: Damit die Kühle nicht "verloren" geht.\n\n' +
      'Die Lüftungsanlage erhöht ggf. den Luftvolumenstrom, um mehr kühle Außenluft einzubringen. Die Regelung vergleicht ständig die Temperaturen und passt den Betrieb an. ' +
      'Wenn eine mechanische Kältemaschine im System ist, wird diese erst zugeschaltet, wenn die freie Kühlung über Außenluft nicht mehr ausreicht.'
  },
  {
    id: 'block_vorteile',
    heading: 'Vorteile der Freien Kühlung auf einen Blick',
    body:
      '• Sehr energieeffizient: Deutlich geringerer Stromverbrauch als bei Kompressionskältemaschinen.\n' +
      '• Minimale Betriebskosten: Hauptsächlich Kosten für Pumpen- und Ventilatorstrom.\n' +
      '• Umweltfreundlich: Reduziert CO2-Emissionen und den Einsatz von Kältemitteln (bei reiner freier Kühlung).\n' +
      '• Verbesserte Luftqualität: Bei Nachtauskühlung oder Außenluftkühlung durch Lüftungsanlagen.\n' +
      '• Langlebig und wartungsarm: Oft weniger komplexe Technik als aktive Kältemaschinen.'
  },
  {
    id: 'block_nachteile',
    heading: 'Mögliche Nachteile und Grenzen',
    body:
      '• Abhängigkeit von Umgebungsbedingungen: Die Verfügbarkeit und das Temperaturniveau der natürlichen Kältequelle sind entscheidend (Wetter, Jahreszeit).\n' +
      '• Begrenzte Kühlleistung: Reicht nicht immer aus, um Spitzenlasten an heißen Tagen komplett abzudecken. Oft als Ergänzung zu einer aktiven Kühlung.\n' +
      '• Regelungsaufwand: Eine intelligente Regelung ist notwendig, um die freie Kühlung optimal zu nutzen.\n' +
      '• Platzbedarf: Erdkollektoren benötigen viel Fläche, Brunnenanlagen sind nicht überall möglich.\n' +
      '• Genehmigungen: Für Grundwassernutzung oder tiefe Erdsonden sind oft Genehmigungen erforderlich.\n' +
      '• Taupunkt: Bei Flächenkühlung muss der Taupunkt genau überwacht werden.'
  },
  {
    id: 'block_fazit',
    heading: 'Fazit',
    body:
      'Freie Kühlung ist eine clevere und nachhaltige Methode, um Gebäude energieeffizient zu kühlen. Sie ist oft ein wichtiger Baustein in modernen Energiekonzepten und kann, ' +
      'richtig geplant und umgesetzt, erheblich zur Reduzierung von Betriebskosten und Umweltbelastung beitragen. ' +
      'Häufig ist eine Kombination mit einer konventionellen Kältemaschine sinnvoll, die dann nur noch bei Bedarf zugeschaltet wird.'
  }
];

const M3_U1_X1_FreieKaelte: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM3, canUploadImagesM3 } = useAuth();
  const canEditTexts = canEditTextsM3;
  const canUploadImages = canUploadImagesM3;
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const mergedBlocks = React.useMemo(() => 
    textBlocks.map(tb => ({
      ...tb,
      body: override?.texts?.[tb.id] || tb.body
    })),
    [override]
  );

  const fullText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = React.useCallback(async (blockId: string, body: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: body } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = React.useCallback(async (key: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, key, file, token);
      alert('Bild aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '3.1.1 Freie Kühlung', actions: ttsBtn },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 2 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          alt: img.alt,
          label: 'Bild einfügen',
            onReplace: canUploadImages ? (file: File) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map(tb => React.createElement(EditableTextBlock, {
          key: tb.id,
          pageId: PAGE_ID,
          blockId: tb.id,
          heading: tb.heading,
          body: tb.body,
          onSave: (body: string) => handleTextSave(tb.id, body)
        }))
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m3_u1_x99_quiz') }, 'Zum Quiz')
    )
  );
};

export default M3_U1_X1_FreieKaelte;

